'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface SignUpFields {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

interface SignUpFormProps {
  onSuccess: () => void;
}

export default function SignUpForm({ onSuccess }: SignUpFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpFields>();

  const password = watch('password');

  // Backend integration point: replace with Supabase signUp call
  const onSubmit = async (data: SignUpFields) => {
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1400));
    toast.success('Account created — please check your email to confirm');
    setLoading(false);
    onSuccess();
  };

  const strength = (pw: string) => {
    if (!pw) return 0;
    let s = 0;
    if (pw.length >= 8) s++;
    if (/[A-Z]/.test(pw)) s++;
    if (/[0-9]/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    return s;
  };

  const pwStrength = strength(password || '');
  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'][pwStrength];
  const strengthColor = ['', 'bg-danger', 'bg-warning', 'bg-info', 'bg-success'][pwStrength];

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
      <div>
        <h2 className="text-2xl font-700 text-foreground mb-1">Create your account</h2>
        <p className="text-sm text-muted-foreground">Start managing cases with AI assistance</p>
      </div>

      {/* Full name */}
      <div>
        <label htmlFor="signup-name" className="block text-sm font-500 text-foreground mb-1.5">
          Full name
        </label>
        <input
          id="signup-name"
          type="text"
          autoComplete="name"
          placeholder="Sarah Chambers"
          className={`w-full px-3.5 py-2.5 rounded-lg border text-sm bg-card focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${
            errors.fullName ? 'border-danger' : 'border-input hover:border-muted-foreground'
          }`}
          {...register('fullName', { required: 'Full name is required' })}
        />
        {errors.fullName && (
          <p role="alert" className="text-xs text-danger mt-1">{errors.fullName.message}</p>
        )}
      </div>

      {/* Email */}
      <div>
        <label htmlFor="signup-email" className="block text-sm font-500 text-foreground mb-1.5">
          Email address
        </label>
        <input
          id="signup-email"
          type="email"
          autoComplete="email"
          placeholder="you@chambers.co.uk"
          className={`w-full px-3.5 py-2.5 rounded-lg border text-sm bg-card focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${
            errors.email ? 'border-danger' : 'border-input hover:border-muted-foreground'
          }`}
          {...register('email', {
            required: 'Email address is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && (
          <p role="alert" className="text-xs text-danger mt-1">{errors.email.message}</p>
        )}
      </div>

      {/* Password */}
      <div>
        <label htmlFor="signup-password" className="block text-sm font-500 text-foreground mb-1.5">
          Password
        </label>
        <p className="text-xs text-muted-foreground mb-1.5">
          Minimum 8 characters with uppercase, number, and symbol
        </p>
        <div className="relative">
          <input
            id="signup-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="••••••••••••"
            className={`w-full px-3.5 py-2.5 pr-10 rounded-lg border text-sm bg-card focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${
              errors.password ? 'border-danger' : 'border-input hover:border-muted-foreground'
            }`}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Must be at least 8 characters' },
            })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            {showPassword ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
          </button>
        </div>
        {password && (
          <div className="mt-2 flex items-center gap-2">
            <div className="flex gap-1 flex-1">
              {[1, 2, 3, 4].map((n) => (
                <div
                  key={`strength-${n}`}
                  className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                    n <= pwStrength ? strengthColor : 'bg-border'
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-muted-foreground">{strengthLabel}</span>
          </div>
        )}
        {errors.password && (
          <p role="alert" className="text-xs text-danger mt-1">{errors.password.message}</p>
        )}
      </div>

      {/* Confirm password */}
      <div>
        <label htmlFor="signup-confirm" className="block text-sm font-500 text-foreground mb-1.5">
          Confirm password
        </label>
        <div className="relative">
          <input
            id="signup-confirm"
            type={showConfirm ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="••••••••••••"
            className={`w-full px-3.5 py-2.5 pr-10 rounded-lg border text-sm bg-card focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${
              errors.confirmPassword ? 'border-danger' : 'border-input hover:border-muted-foreground'
            }`}
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (v) => v === password || 'Passwords do not match',
            })}
          />
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            {showConfirm ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
          </button>
        </div>
        {errors.confirmPassword && (
          <p role="alert" className="text-xs text-danger mt-1">{errors.confirmPassword.message}</p>
        )}
      </div>

      {/* Terms */}
      <div className="flex items-start gap-2">
        <input
          id="terms"
          type="checkbox"
          className="w-4 h-4 mt-0.5 rounded border-input accent-secondary"
          {...register('terms', { required: 'You must accept the terms to continue' })}
        />
        <label htmlFor="terms" className="text-sm text-muted-foreground leading-relaxed">
          I agree to LAICSAI regulation's{' '}
          <button type="button" className="text-secondary hover:underline">Terms of Service</button>{' '}
          and{' '}
          <button type="button" className="text-secondary hover:underline">Privacy Policy</button>
        </label>
      </div>
      {errors.terms && (
        <p role="alert" className="text-xs text-danger -mt-2">{errors.terms.message}</p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full py-2.5 bg-primary text-white text-sm font-600 rounded-lg hover:bg-primary/90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        style={{ minHeight: '42px' }}
      >
        {loading ? (
          <>
            <Loader2 size={15} className="animate-spin" aria-hidden="true" />
            <span>Creating account…</span>
          </>
        ) : (
          'Create Account'
        )}
      </button>
    </form>
  );
}